import {
  contractTypeLabel,
  EMPLOYEE_FUNCTION_LABELS,
} from "@etudescarre/constants";
import { Button, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { TrashIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { useYardContext } from "@/providers/yard-context";

type TeamMember = {
  id: number;
  employeeId: string;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  isReferent: boolean;
};

type Team = {
  id: number;
  name: string;
  description: string | null;
  members: TeamMember[];
};

type EmployeeContract = {
  id: number;
  employeeId: string;
  firstName: string | null;
  lastName: string | null;
  mail: string | null;
  function: string | null;
  isReferent: boolean;
  isActive: boolean;
};

type Contract = {
  id: number;
  type: string;
  code: string | null;
  isActive: boolean;
  isLead: boolean;
  performerCompanyName: string | null;
  teams: Team[];
  employeeContracts: EmployeeContract[];
};

type DeleteTarget =
  | { kind: "contract"; contractId: number; label: string }
  | { kind: "team"; contractId: number; teamId: number; label: string }
  | {
      kind: "member";
      contractId: number;
      teamId: number;
      memberId: number;
      label: string;
    }
  | {
      kind: "referent";
      contractId: number;
      employeeContractId: number;
      label: string;
    };

export function ContratsEquipes() {
  const { yardId } = useParams<{ yardId: string }>();
  const { yard } = useYardContext();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isCommissioned = isNotNil(yard.comissioningDate);

  const fetchContracts = useCallback(async () => {
    if (isNil(yardId)) return;
    setLoading(true);
    const { data } = await apiClient.GET("/api/yards/{yardId}/contracts", {
      params: { path: { yardId } },
    });
    if (isNotNil(data)) {
      setContracts(data.data.contracts);
    }
    setLoading(false);
  }, [yardId]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const handleDelete = async () => {
    if (isNil(deleteTarget) || isNil(yardId)) return;
    setDeleting(true);

    let success = false;

    if (deleteTarget.kind === "contract") {
      const { error } = await apiClient.DELETE(
        "/api/yards/{yardId}/contracts/{contractId}",
        {
          params: {
            path: { yardId, contractId: deleteTarget.contractId },
          },
        },
      );
      success = isNil(error);
    } else if (deleteTarget.kind === "team") {
      const { error } = await apiClient.DELETE(
        "/api/yards/{yardId}/contracts/{contractId}/teams/{teamId}",
        {
          params: {
            path: {
              yardId,
              contractId: deleteTarget.contractId,
              teamId: deleteTarget.teamId,
            },
          },
        },
      );
      success = isNil(error);
    } else if (deleteTarget.kind === "member") {
      const { error } = await apiClient.DELETE(
        "/api/yards/{yardId}/contracts/{contractId}/teams/{teamId}/members/{memberId}",
        {
          params: {
            path: {
              yardId,
              contractId: deleteTarget.contractId,
              teamId: deleteTarget.teamId,
              memberId: deleteTarget.memberId,
            },
          },
        },
      );
      success = isNil(error);
    } else if (deleteTarget.kind === "referent") {
      const { error } = await apiClient.DELETE(
        "/api/yards/{yardId}/contracts/{contractId}/referent/{employeeContractId}",
        {
          params: {
            path: {
              yardId,
              contractId: deleteTarget.contractId,
              employeeContractId: deleteTarget.employeeContractId,
            },
          },
        },
      );
      success = isNil(error);
    }

    setDeleting(false);
    setDeleteTarget(null);

    if (success) {
      await fetchContracts();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-sm text-zinc-400">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Contrats et équipes
        </h1>
        {!isCommissioned && (
          <Button
            onClick={() => setDeleteMode(!deleteMode)}
            className={
              deleteMode
                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                : ""
            }
          >
            {deleteMode ? "Quitter le mode suppression" : "Mode suppression"}
          </Button>
        )}
      </div>

      {contracts.length === 0 ? (
        <p className="text-sm text-zinc-400">Aucun contrat</p>
      ) : (
        <div className="space-y-6">
          {contracts.map((ct) => (
            <ContractCard
              key={ct.id}
              contract={ct}
              deleteMode={deleteMode}
              onDelete={setDeleteTarget}
            />
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal.Root
        open={isNotNil(deleteTarget)}
        onOpenChange={(e) => {
          if (!e.open) setDeleteTarget(null);
        }}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content size="sm">
              <Modal.Header>
                <Modal.Title>Confirmer la suppression</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  Voulez-vous vraiment supprimer{" "}
                  <span className="font-medium">{deleteTarget?.label}</span> ?
                </p>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
                >
                  {deleting ? "Suppression..." : "Supprimer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </div>
  );
}

type ContractCardProps = {
  contract: Contract;
  deleteMode: boolean;
  onDelete: (target: DeleteTarget) => void;
};

function ContractCard({ contract, deleteMode, onDelete }: ContractCardProps) {
  const referents = contract.employeeContracts.filter((ec) => ec.isReferent);

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-800">
      <div className="flex items-center justify-between border-b border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
        <div>
          <h2 className="font-medium text-zinc-900 dark:text-zinc-100">
            {contract.performerCompanyName ?? "Entreprise inconnue"}
          </h2>
          <p className="text-xs text-zinc-500">
            {contractTypeLabel(contract.type)}
            {isNotNil(contract.code) ? ` - ${contract.code}` : ""}
          </p>
        </div>
        {deleteMode && (
          <button
            type="button"
            onClick={() =>
              onDelete({
                kind: "contract",
                contractId: contract.id,
                label: `le contrat ${contract.performerCompanyName ?? contract.id}`,
              })
            }
            className="rounded p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <TrashIcon className="size-4" />
          </button>
        )}
      </div>

      <div className="p-4">
        {/* Referents */}
        {referents.length > 0 && (
          <div className="mb-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Referents
            </h3>
            <div className="space-y-1">
              {referents.map((ec) => (
                <div
                  key={ec.id}
                  className="flex items-center justify-between rounded px-2 py-1 text-sm"
                >
                  <span className="text-zinc-700 dark:text-zinc-300">
                    {ec.firstName} {ec.lastName}
                    {isNotNil(ec.function) && (
                      <span className="ml-2 text-xs text-zinc-400">
                        ({EMPLOYEE_FUNCTION_LABELS[ec.function] ?? ec.function})
                      </span>
                    )}
                  </span>
                  {deleteMode && (
                    <button
                      type="button"
                      onClick={() =>
                        onDelete({
                          kind: "referent",
                          contractId: contract.id,
                          employeeContractId: ec.id,
                          label: `le referent ${ec.firstName} ${ec.lastName}`,
                        })
                      }
                      className="rounded p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <TrashIcon className="size-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Teams */}
        {contract.teams.length > 0 && (
          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Equipes
            </h3>
            <div className="space-y-3">
              {contract.teams.map((team) => (
                <TeamSection
                  key={team.id}
                  team={team}
                  contractId={contract.id}
                  deleteMode={deleteMode}
                  onDelete={onDelete}
                />
              ))}
            </div>
          </div>
        )}

        {contract.teams.length === 0 && referents.length === 0 && (
          <p className="text-sm text-zinc-400">Aucune equipe ni referent</p>
        )}
      </div>
    </div>
  );
}

type TeamSectionProps = {
  team: Team;
  contractId: number;
  deleteMode: boolean;
  onDelete: (target: DeleteTarget) => void;
};

function TeamSection({
  team,
  contractId,
  deleteMode,
  onDelete,
}: TeamSectionProps) {
  return (
    <div className="rounded border border-zinc-100 dark:border-zinc-800">
      <div className="flex items-center justify-between bg-zinc-50/50 px-3 py-2 dark:bg-zinc-800/50">
        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
          {team.name}
        </span>
        {deleteMode && (
          <button
            type="button"
            onClick={() =>
              onDelete({
                kind: "team",
                contractId,
                teamId: team.id,
                label: `l'equipe ${team.name}`,
              })
            }
            className="rounded p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <TrashIcon className="size-3.5" />
          </button>
        )}
      </div>
      {team.members.length > 0 ? (
        <div className="px-3 py-2">
          {team.members.map((member) => (
            <div
              key={member.id}
              className="flex items-center justify-between py-1 text-sm"
            >
              <span className="text-zinc-600 dark:text-zinc-400">
                {member.firstName} {member.lastName}
                {member.isReferent && (
                  <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                    Referent
                  </span>
                )}
              </span>
              {deleteMode && (
                <button
                  type="button"
                  onClick={() =>
                    onDelete({
                      kind: "member",
                      contractId,
                      teamId: team.id,
                      memberId: member.id,
                      label: `le membre ${member.firstName} ${member.lastName}`,
                    })
                  }
                  className="rounded p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <TrashIcon className="size-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="px-3 py-2">
          <p className="text-xs text-zinc-400">Aucun membre</p>
        </div>
      )}
    </div>
  );
}
